import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  useTheme,
  useMediaQuery,
  Collapse,
  Button,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  isDrawerOpen,
  selectIsDrawerOpen,
  selectUserDetail,
} from "../redux/slice/userSlice";
import {
  IconChevronDown,
  IconChevronRight,
  IconMenuDeep,
} from "@tabler/icons-react";
import { lightPalette } from "../theme-layout/palette";
import { menuConfig } from "../components/menu/menuConfig";

const drawerWidth = 240;
const hasMenuAccess = (roles, allowedRoles) => {
  return allowedRoles.some(role => role === roles);
}

const hasActiveClass = (titles, currentTitle) => {
  if (!Array.isArray(titles)) return false;
  return titles.some(title => title === currentTitle);
};

const SideBar = ({ title }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const getUserdetail = useSelector(selectUserDetail);
  const getIsDrawerOpen = useSelector(selectIsDrawerOpen);
  const [openMenu, setOpenMenu] = useState({
    Admin: title === "Users" || title === "State - City",
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openAdminMenu, setOpenAdminMenu] = useState(false);

  const handleAdminClick = () => {
    setOpenAdminMenu(!openAdminMenu);
  };
  const handleDrawerToggle = () => {
    dispatch(isDrawerOpen({ isDrawerOpen: !getIsDrawerOpen }));
  };

  const toggleMenu = (menuTitle) => {
    setOpenMenu((prev) => ({ ...prev, [menuTitle]: !prev[menuTitle] }));
  };

  useEffect(() => {
    if (!isMobile) {
      dispatch(isDrawerOpen({ isDrawerOpen: true }));
    }
  }, []);

  const drawer = (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={2.5}
        sx={{ p: 1.5 }}
      >
        <img
          src="/Cambridge-Caregivers-Logo-2 1.svg"
          alt="Logo"
          style={{ width: "75%" }}
        />
        <IconButton
          edge="start"
          color="inherit"
          onClick={handleDrawerToggle}
          sx={{ pr: 0, display: !getIsDrawerOpen ? "none" : "inline-flex" }}
        >
          <IconMenuDeep color={lightPalette.primary.main} />
        </IconButton>
      </Box>
      <Box flex="1 1 0" display="flex" flexDirection="column" py={2}>
        <List
          sx={{
            flex: "1 1 0",
            px: 1,
            gap: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {menuConfig.map((item) => {
            if (!hasMenuAccess(getUserdetail.roles, item.roles)) return null;

            const isActive = hasActiveClass(item.title, title);
            const hasChildren =
              Array.isArray(item.children) && item.children.length > 0;

            return (
              <div key={item.sideMenuTitle}>
                {hasChildren ? (
                  <>
                    <ListItem
                      component={Button}
                      onClick={() => toggleMenu(item.sideMenuTitle)}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.sideMenuTitle} />
                      {openMenu[item.sideMenuTitle] ? (
                        <IconChevronDown />
                      ) : (
                        <IconChevronRight />
                      )}
                    </ListItem>
                    <Collapse
                      in={openMenu[item.sideMenuTitle]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <List component="div" disablePadding>
                        {item.children
                          .filter((sub) =>
                            hasMenuAccess(getUserdetail.roles, sub.roles)
                          )
                          .map((sub) => (
                            <ListItem
                              component={Button}
                              key={sub.sideMenuTitle}
                              className={
                                hasActiveClass(sub.title, title) ? "active" : ""
                              }
                              onClick={() => {navigate(sub.path); isMobile && handleDrawerToggle()}}
                              sx={{ pl: 4 }}
                            >
                              <ListItemIcon>{sub.icon}</ListItemIcon>
                              <ListItemText primary={sub.sideMenuTitle} />
                            </ListItem>
                          ))}
                      </List>
                    </Collapse>
                  </>
                ) : (
                  <ListItem
                    component={Button}
                    onClick={() => {navigate(item.path); isMobile && handleDrawerToggle()}}
                    className={isActive ? "active" : ""}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.sideMenuTitle} />
                  </ListItem>
                )}
              </div>
            );
          })}
        </List>
      </Box>
    </>
  );

  return (
    <>
      <Drawer
        open={getIsDrawerOpen}
        variant={isMobile ? "temporary" : "persistent"}
        ModalProps={{
          disableEscapeKeyDown: isMobile ? false : true,
          hideBackdrop: isMobile ? false : true,
        }}
        onClose={() => dispatch(isDrawerOpen({ isDrawerOpen: false }))}
        sx={{
          zIndex: (theme) => (isMobile ? theme.zIndex.modal : 1099),
          width: drawerWidth,
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            border: "none",
          },
        }}
        slotProps={{
          paper: {
            elevation: 3,
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default SideBar;
